import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useGeneratorContext } from '../contexts/GeneratorStateContext';
import { GEN_ROOT_SEGMENT } from '../utils/buildRoutes';

const {
  getLangFromPath,
  getPathRest,
  getFirstSegment,
  getStringsForPage,
  getBuildTitle,
  hasServerPageMeta,
  // CommonJS, and outside src/ on purpose — see craco.config.js.
} = require('../../seoStrings');

/** Update document.title and meta description on client-side route changes.
 *  Crawlers still get server-injected meta from server.js; this is for the
 *  browser tab and share-from-tab only. */
export function usePageSeo() {
  const { pathname } = useLocation();
  const { build, buildName } = useGeneratorContext();
  const lvl = typeof build?.getLvl === 'function' ? build.getLvl() : build?.lvl;
  const isFirstRun = useRef(true);

  useEffect(() => {
    const firstRun = isFirstRun.current;
    isFirstRun.current = false;
    const lang = getLangFromPath(pathname);
    const pathRest = pathname === '/' ? '/' : getPathRest(pathname, lang);
    // On the first render the document still carries the server-injected meta,
    // which for build/creator pages is per-page (the build name) and better than
    // anything we can derive here — on a shared build the context is still the
    // empty default until the fetch lands. Overwriting it would drop the name from
    // the tab AND from what Googlebot records after it renders the page. This
    // effect re-runs once the build loads (name/lvl are deps), so the tab still
    // ends up on the generator title below. On later client navigations there is
    // no server meta for the new route, so fall through to the generic strings
    // rather than leaving the previous page's title in place.
    if (firstRun && hasServerPageMeta(pathRest)) return;
    const s = getStringsForPage(lang, pathRest);
    // The generator tab is named after the build being edited, not the page:
    // "Panoplie Kolosso 200 | dofgen". Unnamed builds fall back to "Générateur".
    document.title = getFirstSegment(pathRest) === GEN_ROOT_SEGMENT
      ? getBuildTitle(lang, buildName, lvl)
      : s.title;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', s.description);
  }, [pathname, buildName, lvl]);
}
